import React, { useEffect, useRef } from 'react';

function hexToRgba(hex, alpha) {
  const clean = (hex || '').replace('#', '').trim();
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = Number.parseInt(full || 'ffffff', 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function StarsBackground({
  starDensity = 0.00015,
  allStarsTwinkle = true,
  twinkleProbability = 0.7,
  minTwinkleSpeed = 0.5,
  maxTwinkleSpeed = 1.0,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let raf = 0;
    let width = 0;
    let height = 0;
    let stars = [];
    const dpr = window.devicePixelRatio || 1;

    const root = getComputedStyle(document.documentElement);
    const accent = root.getPropertyValue('--accent').trim() || '#07dbff';
    const accent3 = root.getPropertyValue('--accent3').trim() || '#ff2fcf';

    const colors = [
      'rgba(255,255,255,0.85)',
      hexToRgba(accent, 0.8),
      hexToRgba(accent3, 0.75),
    ];

    const resize = () => {
      const parent = canvas.parentElement;
      width = parent ? parent.clientWidth : window.innerWidth;
      height = parent ? parent.clientHeight : window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.max(28, Math.floor(width * height * starDensity));
      stars = Array.from({ length: count }, () => {
        const twinkle = allStarsTwinkle || Math.random() < twinkleProbability;
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          r: 0.5 + Math.random() * 1.8,
          color: colors[Math.floor(Math.random() * colors.length)],
          baseAlpha: 0.25 + Math.random() * 0.65,
          twinkle,
          phase: Math.random() * Math.PI * 2,
          speed:
            twinkle
              ? minTwinkleSpeed + Math.random() * (maxTwinkleSpeed - minTwinkleSpeed)
              : 0,
        };
      });
    };

    const tick = (t) => {
      ctx.clearRect(0, 0, width, height);
      const time = t / 1000;

      for (let i = 0; i < stars.length; i += 1) {
        const s = stars[i];
        const twinkleFactor = s.twinkle ? 0.55 + 0.45 * Math.sin(time * (2 + s.speed * 2) + s.phase) : 1;
        const alpha = Math.max(0.06, s.baseAlpha * twinkleFactor);

        ctx.beginPath();
        ctx.fillStyle = s.color.replace(/[\d.]+\)$/, `${alpha})`);
        ctx.shadowColor = s.color.replace(/[\d.]+\)$/, `${Math.min(alpha + 0.12, 1)})`);
        ctx.shadowBlur = 6;
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.shadowBlur = 0;
      raf = window.requestAnimationFrame(tick);
    };

    resize();
    raf = window.requestAnimationFrame(tick);

    const onResize = () => resize();
    window.addEventListener('resize', onResize);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [allStarsTwinkle, maxTwinkleSpeed, minTwinkleSpeed, starDensity, twinkleProbability]);

  return <canvas ref={canvasRef} className="stars-background-canvas" aria-hidden="true" />;
}
